import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCreditCard } from '@/icons';
import { Modal } from './Modal';
import { useTheme } from '../contexts/ThemeContext';

interface BillingReadOnlyModalProps {
    isOpen: boolean;
    /** The user can pay for the team; shows the Pay now button. */
    canManageBilling: boolean;
    /** Go to the team billing tab. */
    onPayNow: () => void;
    /** Close and keep viewing. */
    onClose: () => void;
    /** Label for the close button. */
    closeLabel?: string;
}

/**
 * BillingDunning: shown when the server refuses a write because the team's grace period ended
 * with a renewal unpaid. Not dismissible by clicking outside -- the user must choose.
 */
export const BillingReadOnlyModal: React.FC<BillingReadOnlyModalProps> = ({
    isOpen,
    canManageBilling,
    onPayNow,
    onClose,
    closeLabel = 'Continue in View-Only Mode',
}) => {
    const theme = useTheme();

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => { /* not dismissible -- the user must choose */ }}
            title="Account Is Read Only"
            showCloseButton={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'flex-start' }}>
                    <FontAwesomeIcon
                        icon={faLock}
                        style={{ color: theme.colors.status.error, fontSize: theme.typography.fontSize.xl, marginTop: '2px' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <p style={{ margin: 0, color: theme.colors.text.primary }}>
                            Your account is read-only until your subscription is paid.
                        </p>
                        <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                            {canManageBilling
                                ? 'You can still open cases and download documents. Pay now to make changes again.'
                                : 'You can still open cases and download documents. Contact your firm administrator to pay.'}
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.border.default}`,
                            backgroundColor: theme.colors.surface,
                            color: theme.colors.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        {closeLabel}
                    </button>
                    {canManageBilling && (
                        <button
                            type="button"
                            onClick={onPayNow}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: theme.spacing.xs,
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                borderRadius: theme.borderRadius.sm,
                                border: `1px solid ${theme.colors.primary}`,
                                backgroundColor: theme.colors.primary,
                                color: theme.colors.text.inverse,
                                cursor: 'pointer',
                            }}
                        >
                            <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: theme.typography.fontSize.sm }} />
                            Pay now
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
};
